/**
 * RecentSignups.tsx — /admin/sign-ups. PlatformOwner-only, read-only widget listing who has recently
 * registered on the platform (their first sign-in), with whether each has accepted the Terms of Use in
 * force now. Mounted as an in-place modal panel over the Kombi Admin Tools page (RecentSignupsPanel),
 * the same host contract every admin governance panel uses.
 *
 * WHY A WIDGET AND NOT A NOTIFICATION. The ask was "let us see when a new user registers / accepts
 * T&Cs". The platform's notification vocabulary is closed and deliberately small, so a new arrival is
 * answered by a place the owner can look rather than a message pushed at them — a read model over
 * `app.app_user.first_seen`, which is stamped once at first sign-in and never moved again, so it is the
 * honest "first logged in" date. `last_seen` is shown beside it for context (it moves every sign-in).
 *
 * READ-ONLY. There is no action on this panel — it lists, it does not change anything, and the route
 * behind it writes nothing. Departure and its undo live on their own owner surfaces; this is only a
 * window onto arrivals.
 *
 * The panel fetches its own data on open via useOverlayData (the page does not preload it). The
 * still-registered /admin/sign-ups route renders through a thin RecentSignups() wrapper that opens the
 * same panel over a blank backdrop for the deep-link path, exactly as Headcount does.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, type RecentRegistration, type RecentRegistrationsPayload } from './api';
import { shortWhen } from './shared';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  ProvisionalNote,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type RecentSignupsLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | { state: 'ok'; payload: RecentRegistrationsPayload };

export async function recentSignupsLoader(): Promise<RecentSignupsLoaderData> {
  const res = await apiGet<RecentRegistrationsPayload>('/api/admin/users/recent-registrations');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/users/recent-registrations failed (${res.status})`);
  }
  return { state: 'ok', payload: res.body };
}

/**
 * The overlay body the Kombi Admin page mounts. Fetches on open (useOverlayData → recentSignupsLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the list on success.
 */
export function RecentSignupsPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error } = useOverlayData(recentSignupsLoader);

  return (
    <KombiAdminOverlay title="Sign-ups" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading sign-ups…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the list could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Sign-ups" />
      ) : data && data.state === 'ok' ? (
        <RecentSignupsBody payload={data.payload} />
      ) : null}
    </KombiAdminOverlay>
  );
}

/** How one person's acceptance of the current Terms of Use reads, in the owner's terms. */
function termsLine(row: RecentRegistration, hasPublishedPolicy: boolean): string {
  if (!hasPublishedPolicy) return 'No Terms of Use published yet';
  return row.acceptedCurrentTermsAt === null
    ? 'Has not accepted the current Terms of Use'
    : `Accepted the current Terms of Use ${shortWhen(row.acceptedCurrentTermsAt)}`;
}

/** The ok-state body: the intro notice and the newest-first list of registrations. */
function RecentSignupsBody({ payload }: { payload: RecentRegistrationsPayload }) {
  const hasPublishedPolicy = payload.currentTermsVersionId !== null;

  return (
    <div>
      <ProvisionalNote />
      <Notice>
        Who has registered on the platform, newest first — the date is their first sign-in. It also
        shows whether each person has accepted the Terms of Use in force now
        {payload.currentTermsVersionLabel !== null ? ` (${payload.currentTermsVersionLabel})` : ''}.
        This is a read-only view; it names people and is only visible to the platform owner.
      </Notice>

      <SectionHeader>Recent sign-ups</SectionHeader>
      {payload.registrations.length === 0 ? (
        <EmptyState>Nobody has registered yet.</EmptyState>
      ) : (
        <ul className="kombi-dialog-stack">
          {payload.registrations.map((row) => (
            <li key={row.userId} className="kombi-admin-card">
              <div>
                {row.displayName}
                {row.status === 'Departed' ? ' — departed' : ''}
              </div>
              <p className="learn-note">
                First signed in {shortWhen(row.firstSeen)} · last seen {shortWhen(row.lastSeen)}
              </p>
              <p className="learn-note">{termsLine(row, hasPublishedPolicy)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/sign-ups deep link. Opens the panel always-open
 * over a blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function RecentSignups() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <RecentSignupsPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
